import type { DirectPurchaseStatus } from './types/directPurchase';

import { RentalDealStatus } from './types/rental';
import { DeliveryDealStatus } from './types/delivery';

export const rentalStatusMap: Record<
  RentalDealStatus,
  { label: string; shortLabel: string; color: string; textColor: string }
> = {
  // Deploy lifecycle (backend-driven)
  [RentalDealStatus.CREATED]: {
    label: 'Rental created - preparing escrow',
    shortLabel: 'Created',
    color: '#6B7280',
    textColor: 'white',
  },
  [RentalDealStatus.DEPLOY_INITIATED]: {
    label: 'Preparing escrow (deploying contract)',
    shortLabel: 'Deploying',
    color: '#8B5CF6',
    textColor: 'white',
  },
  [RentalDealStatus.DEPLOY_CONFIRMED]: {
    label: 'Preparing escrow (contract deployed)',
    shortLabel: 'Contract Ready',
    color: '#8B5CF6',
    textColor: 'white',
  },
  [RentalDealStatus.DEPLOY_FAILED]: {
    label: 'Escrow deployment failed',
    shortLabel: 'Deploy Failed',
    color: '#EF4444',
    textColor: 'white',
  },
  // On-chain-aligned (mirrored from contract flags)
  [RentalDealStatus.INITIALIZED]: {
    label: 'Escrow initialized - ready for payment',
    shortLabel: 'Initialized',
    color: '#3B82F6',
    textColor: 'white',
  },
  [RentalDealStatus.PAID]: {
    label: 'Paid - waiting for dealer to hand over the car',
    shortLabel: 'Paid',
    color: '#14B8A6',
    textColor: 'white',
  },
  [RentalDealStatus.PICKED_UP]: {
    label: 'Car picked up - finalizing handover',
    shortLabel: 'Picked Up',
    color: '#8B5CF6',
    textColor: 'white',
  },
  [RentalDealStatus.READY_FOR_PICKUP]: {
    label: 'Ready for pickup - confirm to start the rental',
    shortLabel: 'Ready',
    color: '#F59E0B',
    textColor: 'white',
  },
  [RentalDealStatus.ACTIVE]: {
    label: 'Rental active',
    shortLabel: 'Active',
    color: '#22C55E',
    textColor: 'white',
  },
  // Not a penalty (spec §1.1): time past the agreed end is rental outside the
  // agreement, charged per started day at the listing's published 1-3 day rate.
  [RentalDealStatus.OVERDUE]: {
    label: 'Past the end date - charged daily from your deposit',
    shortLabel: 'Overdue',
    color: '#F59E0B',
    textColor: 'white',
  },
  [RentalDealStatus.DEPOSIT_EXHAUSTED]: {
    label: 'Deposit exhausted - return the car now',
    shortLabel: 'Deposit Out',
    color: '#EF4444',
    textColor: 'white',
  },
  [RentalDealStatus.RETURNED]: {
    label: 'Car returned - under inspection',
    shortLabel: 'Returned',
    color: '#8B5CF6',
    textColor: 'white',
  },
  [RentalDealStatus.DEFECTED]: {
    label: 'Damage reported during inspection',
    shortLabel: 'Defected',
    color: '#EF4444',
    textColor: 'white',
  },
  [RentalDealStatus.DISPUTED]: {
    label: 'Dispute opened',
    shortLabel: 'Disputed',
    color: '#EF4444',
    textColor: 'white',
  },
  [RentalDealStatus.COMPLETED]: {
    label: 'Rental completed',
    shortLabel: 'Complete',
    color: '#22C55E',
    textColor: 'white',
  },
  [RentalDealStatus.CANCELLED]: {
    label: 'Rental cancelled',
    shortLabel: 'Cancelled',
    color: '#EF4444',
    textColor: 'white',
  },
};

// Buy Now (direct) purchase verification status. The seller is notified only on
// `verified`; `failed` means the on-chain transfer was not confirmed.
export const directPurchaseStatusMap: Record<
  DirectPurchaseStatus,
  { label: string; shortLabel: string; color: string; textColor: string }
> = {
  pending: {
    label: 'Payment pending on-chain verification',
    shortLabel: 'Pending',
    color: '#F59E0B',
    textColor: 'white',
  },
  verified: {
    label: 'Payment verified',
    shortLabel: 'Paid',
    color: '#22C55E',
    textColor: 'white',
  },
  failed: {
    label: 'Payment could not be verified',
    shortLabel: 'Failed',
    color: '#EF4444',
    textColor: 'white',
  },
};

// Buyer-facing status copy for the delivery vertical (deal v2, spec §4.9). Offer phase
// and cancel finals are platform-driven; `escrowed`..`picked_up` are projected on-chain
// by the poller.
export const deliveryStatusMap: Record<
  DeliveryDealStatus,
  { label: string; shortLabel: string; color: string; textColor: string }
> = {
  [DeliveryDealStatus.Offered]: {
    label: 'Offer sent — waiting for the dealer to confirm',
    shortLabel: 'Offered',
    color: '#3B82F6',
    textColor: 'white',
  },
  [DeliveryDealStatus.Approved]: {
    label: 'Dealer confirmed — confirm to start the payment',
    shortLabel: 'Confirm',
    color: '#CCFF00',
    textColor: 'grey',
  },
  [DeliveryDealStatus.AwaitingPayment]: {
    label: 'Preparing escrow — pay to continue',
    shortLabel: 'Pay',
    color: '#CCFF00',
    textColor: 'grey',
  },
  [DeliveryDealStatus.Escrowed]: {
    label: 'Paid — waiting for the logist to pick up the car',
    shortLabel: 'Escrowed',
    color: '#14B8A6',
    textColor: 'white',
  },
  [DeliveryDealStatus.Disputed]: {
    label: 'Dispute opened — funds held by GAC until resolved',
    shortLabel: 'Disputed',
    color: '#EF4444',
    textColor: 'white',
  },
  [DeliveryDealStatus.PickedUp]: {
    label: 'Car picked up — on its way to the pickup city',
    shortLabel: 'In transit',
    color: '#8B5CF6',
    textColor: 'white',
  },
  [DeliveryDealStatus.Delivered]: {
    label: 'Delivered to the pickup point — ready for handover',
    shortLabel: 'Ready',
    color: '#F59E0B',
    textColor: 'white',
  },
  [DeliveryDealStatus.HandedOver]: {
    label: 'Handed over — deal completed',
    shortLabel: 'Complete',
    color: '#22C55E',
    textColor: 'white',
  },
  [DeliveryDealStatus.OfferExpired]: {
    label: 'Offer expired — the dealer did not confirm in time',
    shortLabel: 'Expired',
    color: '#6B7280',
    textColor: 'white',
  },
  [DeliveryDealStatus.Declined]: {
    label: 'The dealer did not confirm this offer',
    shortLabel: 'Not confirmed',
    color: '#6B7280',
    textColor: 'white',
  },
  [DeliveryDealStatus.Withdrawn]: {
    label: 'You withdrew this offer',
    shortLabel: 'Withdrawn',
    color: '#6B7280',
    textColor: 'white',
  },
  [DeliveryDealStatus.ApprovalExpired]: {
    label: 'Payment window expired — the car is back on sale',
    shortLabel: 'Expired',
    color: '#6B7280',
    textColor: 'white',
  },
  [DeliveryDealStatus.Cancelled]: {
    label: 'Cancelled — price and delivery refunded to you',
    shortLabel: 'Cancelled',
    color: '#EF4444',
    textColor: 'white',
  },
  [DeliveryDealStatus.Refunded]: {
    label: 'Refunded by GAC — price and delivery returned to you',
    shortLabel: 'Refunded',
    color: '#EF4444',
    textColor: 'white',
  },
};

/**
 * Where the wallet session kept its token until the e-mail-only sign-in (api#402, step 2). The
 * storefront no longer signs anyone in with a wallet; a token left under this key by an earlier
 * build is dropped when the app starts (`dropLegacyTokens`) and never sent anywhere.
 */
export const LEGACY_WALLET_TOKEN_KEY = 'accessToken';

/**
 * Where the account session kept its two tokens until the single sign-in (api#454). The session is
 * a cookie the api sets now — the storefront and the dealer cabinet hold it together, and no
 * script on either page can read it — so tokens a build from before left under these keys are
 * dropped when the app starts (`dropLegacyTokens`) and never sent anywhere.
 */
export const LEGACY_USER_ACCESS_TOKEN_KEY = 'userAccessToken';
export const LEGACY_USER_REFRESH_TOKEN_KEY = 'userRefreshToken';

/**
 * Not a credential: `'1'` says this browser signed in here (api#454). The session itself is the
 * api's cookie, which the page cannot see; this hint is what tells the shell to hold the splash and
 * the Buy control until `/user/auth/me` has answered, rather than show a signed-in person the
 * anonymous page for a frame. A wrong hint costs one refused `/me` and is cleared by it.
 */
export const SIGNED_IN_HINT_KEY = 'gac:signed-in';
